import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';

import { IconButton } from '@components/button/Button';
import {
  acceptedLogoInputTypes,
  allowedLogoFileTypes,
  allowedLogoFileTypesLabel
} from '@components/file-upload/constants';
import { Avatar, Button, Icon } from '@components/index';
import {
  allowedImageSize,
  allowedImageSizeLabel,
  excpectedOrganizationLogoWidth,
  excpectedOrganizationLogoWidthHeight
} from '@constants/file';
import { imageInitialState } from '@constants/generic';
import testId from '@constants/testId';
import useDragAndDrop from '@hooks/useDragAndDrop';
import { ImageForm } from '@types';

import ImageCropModal from './ImageCropModal';

import theme from '../../../themes/colors';

interface FileUploadProps {
  orgName: string;
  handleAdd: (file: File) => void;
  handleRemove: () => void;
  setOrgImage: Dispatch<SetStateAction<ImageForm>>;
  setOpenImageCropModal: Dispatch<SetStateAction<boolean>>;
  label: string;
  initialPreviewUrl?: string;
  openImageCropModal: boolean;
  uploadedFile: File | undefined | null;
}

const LogoUpload: FC<FileUploadProps> = ({
  orgName,
  handleAdd,
  setOrgImage,
  handleRemove,
  setOpenImageCropModal,
  label,
  uploadedFile,
  openImageCropModal,
  initialPreviewUrl = ''
}) => {
  const {
    dragOver,
    fileDropError,
    onDragOver,
    onDragLeave,
    setDragOver,
    setFileDropError
  } = useDragAndDrop();

  const inputId = `${label}_input_id`;

  const [fileName, setFileName] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string | undefined>();
  const [file, setFile] = useState<File | undefined | null>(uploadedFile);
  const [previewUrl, setPreviewUrl] = useState<string>(initialPreviewUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValidFile = (selectedFile: File) => {
    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'jpeg') {
      const error = 'Upload failed. Unsupported file type';
      setFileDropError(error);
      return false;
    }

    if (selectedFile.size > allowedImageSize) {
      const error = `Upload failed. File size more than ${allowedImageSizeLabel}`;
      setFileDropError(error);
      return false;
    }

    if (
      selectedFile.type &&
      !allowedLogoFileTypes.includes(selectedFile.type)
    ) {
      const error = 'Upload failed. Unsupported file type';
      setFileDropError(error);
      return false;
    }

    return true;
  };

  const handleFileProcessing = async (selectedFile: File) => {
    setFileDropError('');
    setFileName(selectedFile.name);
    setFileUrl(URL.createObjectURL(selectedFile));
    setOpenImageCropModal(true);
  };

  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile && isValidFile(selectedFile)) {
      handleFileProcessing(selectedFile);
    }
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);

    const selectedFile = e?.dataTransfer?.files[0];
    if (selectedFile && isValidFile(selectedFile)) {
      handleFileProcessing(selectedFile);
    }
  };

  const deleteFile = () => {
    setOrgImage(imageInitialState);
    setFile(undefined);
    setPreviewUrl('');
    handleRemove();
  };

  const onCancelCrop = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setFileUrl('');
    setOpenImageCropModal(false);
  };

  const onConfirmCrop = (croppedFile: File) => {
    const previewUrls = URL.createObjectURL(croppedFile);
    setFile(croppedFile);
    setPreviewUrl(previewUrls);
    setOrgImage({ file: croppedFile, preview: previewUrls });
    setOpenImageCropModal(false);
    handleAdd(croppedFile);
  };

  useEffect(() => {
    if (uploadedFile) setPreviewUrl(URL.createObjectURL(uploadedFile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  useEffect(() => {
    if (initialPreviewUrl) setPreviewUrl(initialPreviewUrl);
  }, [initialPreviewUrl]);

  return (
    <div className="px-6 py-2">
      {!openImageCropModal ? (
        <div className="flex flex-col gap-1">
          <div className="relative flex gap-3">
            <Avatar
              imageId={testId.header.organizationLogo}
              name={orgName}
              imageUrl={file ? URL.createObjectURL(file) : previewUrl}
              className="size-28 min-w-28 rounded-xl"
              textClassName="text-xl font-semibold text-cofeeNeutral10"
              bgColor={theme.cofeePurple50}
            />
            <div className="flex h-28 flex-col justify-between">
              <div className="mb-2">
                <span className="block text-sm font-semibold text-cofeeNeutral10">
                  Business logo
                </span>
                <span className="text-cofeeGreyBlue500 block text-sm">
                  PNG or JPG files only (max 5MB, 295x295 preferred)
                </span>
              </div>
              <input
                type="file"
                id={inputId}
                className="hidden"
                onChange={onFileSelect}
                accept={acceptedLogoInputTypes}
                ref={fileInputRef}
              />
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  label={`${previewUrl ? 'Change logo' : 'Add logo'}`}
                  handleClick={() => fileInputRef.current?.click()}
                  className="w-fit !bg-[#F5FCF8]"
                  leftIconName="image"
                />
                {previewUrl && (
                  <IconButton
                    iconName=""
                    leftIconName="trash-red-3"
                    leftIconStroke={theme.cofeeRed5}
                    labelStyle="!text-cofeeRed5"
                    leftIconStyle="mr-1.5"
                    variant="transparent"
                    handleClick={deleteFile}
                    className="border border-cofeeRed5"
                    label="Delete"
                  />
                )}
              </div>
            </div>
            <div className="hidden items-center gap-x-5 rounded-xl border border-gray-300 p-3">
              <div className="flex items-center justify-center rounded-[5px]">
                <img
                  src={previewUrl}
                  className="object-fit h-25 w-auto rounded-[5px]"
                  alt="branch_logo"
                />
              </div>
              <div>
                <div className="items-center justify-center gap-2">
                  <div className="mt-2 flex justify-center">
                    <div
                      className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-red-50 px-4 py-2 text-sm font-medium text-red-500 transition hover:bg-red-100"
                      role="presentation"
                      onClick={deleteFile}
                    >
                      <Icon
                        name="trash-red-2"
                        size="none"
                        className="size-5 text-red-500"
                      />
                      Remove
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {fileDropError && (
            <div className="left-0 top-28 rounded text-sm text-cofeeRed7">
              {fileDropError}
            </div>
          )}
        </div>
      ) : (
        <>
          {!openImageCropModal && (
            <>
              <span className="mb-1 block text-sm font-medium text-gray-500">
                Business Logo
              </span>
              <input
                type="file"
                id={inputId}
                className="hidden"
                onChange={onFileSelect}
                accept={acceptedLogoInputTypes}
                ref={fileInputRef}
              />
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`rounded-xl border border-dashed bg-white p-3 text-center transition-colors ${
                  dragOver ? 'bg-gray-100' : ''
                } ${
                  fileDropError
                    ? '!border-solid !border-red-500'
                    : 'border-gray-300'
                }`}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    fileInputRef.current?.click();
                  }
                }}
              >
                <div className="flex justify-center">
                  {previewUrl ? (
                    <div className="flex items-center justify-center rounded-md border border-gray-200 p-1">
                      <img
                        src={previewUrl}
                        className="size-16 rounded-md object-cover"
                        alt="branch_logo"
                      />
                    </div>
                  ) : (
                    <label
                      htmlFor="image-input"
                      className="flex cursor-pointer justify-center"
                    >
                      <Icon
                        name="logo-upload"
                        size="none"
                        className="size-16 text-gray-400"
                      />
                    </label>
                  )}
                </div>

                <div className="mt-3 flex flex-col items-center gap-3">
                  <div className="mb-0 text-center">
                    <label className="cursor-pointer text-sm font-medium text-green-600">
                      Click to upload
                    </label>
                    <span className="pl-1 text-sm font-medium text-gray-500">
                      or drag and drop
                    </span>
                  </div>

                  <div className="text-center text-sm text-gray-500">
                    {`${allowedLogoFileTypesLabel} files only (max ${allowedImageSizeLabel}, ${excpectedOrganizationLogoWidth} x ${excpectedOrganizationLogoWidthHeight} preferred)`}
                  </div>
                </div>
              </div>
            </>
          )}

          {fileDropError && (
            <div className="mt-3 rounded bg-red-100 px-4 py-2 text-sm text-red-600">
              {fileDropError}
            </div>
          )}
        </>
      )}

      {openImageCropModal && (
        <ImageCropModal
          onClose={onCancelCrop}
          onConfirm={onConfirmCrop}
          fileName={fileName}
          previewUrl={fileUrl || ''}
          expectedAspectRatio={
            excpectedOrganizationLogoWidth /
            excpectedOrganizationLogoWidthHeight
          }
        />
      )}
    </div>
  );
};
export default LogoUpload;
